import React, { useEffect, useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

import { RootState } from '../../store';

interface AffiliateStats {
  referral_code: string;
  total_referrals: number;
  total_earnings: number;
  pending_earnings: number;
  paid_earnings: number;
  status: string;
  commission_rate: number;
}

interface Referral {
  id: string;
  tenant_name: string;
  tenant_email: string;
  subscription_status: string;
  commission_amount: number;
  commission_status: string;
  created_at: string;
}

interface Invoice {
  id: string;
  tenant: {
    name: string;
    subdomain: string;
  };
  subscription: {
    plan: {
      name: string;
    };
  };
  invoice_number: string;
  status: 'DRAFT' | 'OPEN' | 'PAID' | 'VOID' | 'UNCOLLECTIBLE';
  subtotal: number;
  tax: number;
  total: number;
  due_date: string;
  paid_at: string | null;
  created_at: string;
}

export default function AffiliateDashboard() {
  const { t } = useTranslation('affiliate');
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [copied, setCopied] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Initialize dates with current month (start at 00:00:00, end at 23:59:59)
  const getDefaultStartDate = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split('T')[0];
  };

  const getDefaultEndDate = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const user = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    loadDashboard();
    loadInvoices();
  }, []);

  const loadDashboard = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/v1/affiliates/dashboard`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setStats(data.data.stats);
        setReferrals(data.data.referrals || []);
      } else if (response.status === 403 && data.status === 'suspended') {
        // Handle suspended status - set stats with suspended status to trigger UI
        setStats({
          referral_code: '',
          total_referrals: 0,
          total_earnings: 0,
          pending_earnings: 0,
          paid_earnings: 0,
          status: 'suspended',
        });
      } else if (response.status === 404) {
        // Not an affiliate yet - leave stats as null
        setStats(null);
      }
    } catch (error) {
      console.error('Failed to load affiliate dashboard', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      setIsLoadingInvoices(true);
      const response = await fetch(`${API_BASE_URL}/v1/affiliates/invoices`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        // Convert string values to numbers
        const invoicesWithNumbers = (data.data.invoices || []).map((inv: any) => ({
          ...inv,
          subtotal: Number(inv.subtotal || 0),
          tax: Number(inv.tax || 0),
          total: Number(inv.total || 0),
        }));
        setInvoices(invoicesWithNumbers);
      }
    } catch (error) {
      console.error('Failed to load affiliate invoices', error);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const getReferralLink = () => {
    if (!stats) return '';
    return `${window.location.origin}/?ref=${stats.referral_code}`;
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(getReferralLink());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusBadge = (status: string) => {
    const colors: { [key: string]: string } = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      trialing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      canceled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      paid: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    };

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colors[status.toLowerCase()] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
        </div>
      </MainLayout>
    );
  }

  if (!stats) {
    return (
      <MainLayout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="rounded-lg bg-yellow-50 p-6 dark:bg-yellow-900/20">
            <h2 className="text-xl font-bold text-yellow-800 dark:text-yellow-400">
              {t('dashboard.notAffiliate.title')}
            </h2>
            <p className="mt-2 text-yellow-700 dark:text-yellow-300">
              {t('dashboard.notAffiliate.message')}
            </p>
            <a
              href="https://flux.devskin.com/affiliates"
              className="mt-4 inline-block rounded-md bg-yellow-600 px-4 py-2 text-white hover:bg-yellow-700"
            >
              {t('dashboard.notAffiliate.button')}
            </a>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Check if affiliate is suspended
  if (stats.status === 'suspended') {
    return (
      <MainLayout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="rounded-lg bg-red-50 border-2 border-red-200 p-8 dark:bg-red-900/20 dark:border-red-800">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg className="h-12 w-12 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-red-800 dark:text-red-400 mb-3">
                  {t('dashboard.suspended.title')}
                </h2>
                <p className="text-red-700 dark:text-red-300 text-lg mb-4">
                  {t('dashboard.suspended.message')}
                </p>
                <p className="text-red-600 dark:text-red-400 text-sm">
                  {t('dashboard.suspended.contact')}{' '}
                  <a href={`mailto:${t('dashboard.suspended.email')}`} className="font-semibold underline hover:text-red-700 dark:hover:text-red-300">
                    {t('dashboard.suspended.email')}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Check if affiliate is pending approval
  if (stats.status === 'pending') {
    return (
      <MainLayout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="rounded-lg bg-blue-50 border-2 border-blue-200 p-8 dark:bg-blue-900/20 dark:border-blue-800">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg className="h-12 w-12 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-blue-800 dark:text-blue-400 mb-3">
                  {t('dashboard.pending.title')}
                </h2>
                <p className="text-blue-700 dark:text-blue-300 text-lg mb-4">
                  {t('dashboard.pending.message')}
                </p>
                <p className="text-blue-600 dark:text-blue-400 text-sm">
                  {t('dashboard.pending.notice')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8 space-y-4 sm:space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {t('dashboard.title')}
          </h1>
          <p className="mt-1 sm:mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('dashboard.subtitle')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
          <div className="rounded-lg bg-white p-4 sm:p-6 shadow dark:bg-gray-800">
            <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('dashboard.stats.totalReferrals')}
            </div>
            <div className="mt-1 sm:mt-2 text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              {stats.total_referrals}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 sm:p-6 shadow dark:bg-gray-800">
            <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('dashboard.stats.totalEarnings')}
            </div>
            <div className="mt-1 sm:mt-2 text-xl sm:text-3xl font-bold text-green-600 dark:text-green-400">
              ${(Number(stats.total_earnings) || 0).toFixed(2)}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 sm:p-6 shadow dark:bg-gray-800">
            <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('dashboard.stats.pending')}
            </div>
            <div className="mt-1 sm:mt-2 text-xl sm:text-3xl font-bold text-yellow-600 dark:text-yellow-400">
              ${(Number(stats.pending_earnings) || 0).toFixed(2)}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 sm:p-6 shadow dark:bg-gray-800">
            <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('dashboard.stats.paidOut')}
            </div>
            <div className="mt-1 sm:mt-2 text-xl sm:text-3xl font-bold text-indigo-600 dark:text-indigo-400">
              ${(Number(stats.paid_earnings) || 0).toFixed(2)}
            </div>
          </div>
        </div>

        {/* Referral Link */}
        <div className="rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 p-4 sm:p-6 text-white shadow-lg">
          <h2 className="text-lg sm:text-xl font-bold">{t('dashboard.referralLink.title')}</h2>
          <p className="mt-1 sm:mt-2 text-sm text-indigo-100">
            {t('dashboard.referralLink.subtitle')}
          </p>
          <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              readOnly
              value={getReferralLink()}
              className="w-full sm:flex-1 rounded-md border-0 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-gray-900 focus:outline-none"
            />
            <button
              onClick={copyReferralLink}
              className="w-full sm:w-auto rounded-md bg-white px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-semibold text-indigo-600 hover:bg-indigo-50 transition-colors"
            >
              {copied ? t('dashboard.referralLink.copied') : t('dashboard.referralLink.copy')}
            </button>
          </div>
        </div>

        {/* Commission Structure */}
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t('dashboard.commission.title')}
          </h2>
          <div className="flex items-center justify-center">
            <div className="border-l-4 border-indigo-500 pl-6">
              <div className="text-5xl font-bold text-indigo-600 dark:text-indigo-400">
                {stats.commission_rate}%
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                {t('dashboard.commission.rate')}
              </div>
            </div>
          </div>
        </div>

        {/* Referrals Table */}
        <div className="rounded-lg bg-white shadow dark:bg-gray-800">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('dashboard.referrals.title')}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('dashboard.referrals.customer')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('dashboard.referrals.subscription')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('dashboard.referrals.commission')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('dashboard.referrals.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('dashboard.referrals.date')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {referrals.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      {t('dashboard.referrals.empty')}
                    </td>
                  </tr>
                ) : (
                  referrals.map((referral) => (
                    <tr key={referral.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex flex-col">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {referral.tenant_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {referral.tenant_email}
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        {getStatusBadge(referral.subscription_status)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                        ${(Number(referral.commission_amount) || 0).toFixed(2)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        {getStatusBadge(referral.commission_status)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {new Date(referral.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Invoices Section */}
        <div className="space-y-6">
          {/* Section Header */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('dashboard.invoices.title')}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {t('dashboard.invoices.subtitle')}
            </p>
          </div>

          {/* Date Filters */}
          <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('dashboard.invoices.filters.startDate')}
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('dashboard.invoices.filters.endDate')}
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <button
                onClick={loadInvoices}
                className="rounded-md bg-indigo-600 px-6 py-2 text-white hover:bg-indigo-700 transition-colors"
              >
                {t('dashboard.invoices.filters.apply')}
              </button>
              <button
                onClick={() => {
                  setStartDate(getDefaultStartDate());
                  setEndDate(getDefaultEndDate());
                  loadInvoices();
                }}
                className="rounded-md border border-gray-300 px-6 py-2 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {t('dashboard.invoices.filters.clear')}
              </button>
            </div>
          </div>

          {/* Commission Summary Panel */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {(() => {
              const filteredInvoices = invoices.filter(inv => {
                if (!startDate && !endDate) return true;
                const invDate = new Date(inv.created_at);

                // Start date at 00:00:00
                if (startDate) {
                  const start = new Date(startDate);
                  start.setHours(0, 0, 0, 0);
                  if (invDate < start) return false;
                }

                // End date at 23:59:59
                if (endDate) {
                  const end = new Date(endDate);
                  end.setHours(23, 59, 59, 999);
                  if (invDate > end) return false;
                }

                return true;
              });

              const paidInvoices = filteredInvoices.filter(inv => inv.status === 'PAID');
              const totalAmount = paidInvoices.reduce((sum, inv) => sum + inv.total, 0);
              const yourEarnings = totalAmount * ((stats.commission_rate || 25) / 100);

              return (
                <>
                  <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('dashboard.invoices.summary.totalInvoices')}
                    </div>
                    <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                      {filteredInvoices.length}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('dashboard.invoices.summary.paidInvoices')}
                    </div>
                    <div className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">
                      {paidInvoices.length}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('dashboard.invoices.summary.totalAmount')}
                    </div>
                    <div className="mt-2 text-3xl font-bold text-blue-600 dark:text-blue-400">
                      ${totalAmount.toFixed(2)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 p-6 shadow-lg">
                    <div className="text-sm font-medium text-indigo-100">
                      {t('dashboard.invoices.summary.yourEarnings')}
                    </div>
                    <div className="mt-2 text-3xl font-bold text-white">
                      ${yourEarnings.toFixed(2)}
                    </div>
                    <div className="mt-1 text-xs text-indigo-100">
                      {stats.commission_rate}% commission
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Payment Information */}
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-6 dark:bg-blue-900/20 dark:border-blue-800">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-400 mb-3">
              {t('dashboard.invoices.payment.title')}
            </h3>
            <p className="text-blue-800 dark:text-blue-300 mb-2">
              {t('dashboard.invoices.payment.message')}{' '}
              <a
                href={`mailto:${t('dashboard.invoices.payment.email')}`}
                className="font-bold underline hover:text-blue-600 dark:hover:text-blue-200"
              >
                {t('dashboard.invoices.payment.email')}
              </a>
              .
            </p>
            <p className="text-blue-700 dark:text-blue-400 text-sm">
              {t('dashboard.invoices.payment.messageEnd')}
            </p>
          </div>

          {/* Invoices Table */}
          <div className="rounded-lg bg-white shadow dark:bg-gray-800">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('dashboard.invoices.title')}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('dashboard.invoices.table.invoice')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('dashboard.invoices.table.customer')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('dashboard.invoices.table.plan')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('dashboard.invoices.table.amount')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('dashboard.invoices.table.yourCommission')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('dashboard.invoices.table.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('dashboard.invoices.table.date')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {isLoadingInvoices ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
                        </div>
                      </td>
                    </tr>
                  ) : (() => {
                    const filteredInvoices = invoices.filter(inv => {
                      if (!startDate && !endDate) return true;
                      const invDate = new Date(inv.created_at);

                      // Start date at 00:00:00
                      if (startDate) {
                        const start = new Date(startDate);
                        start.setHours(0, 0, 0, 0);
                        if (invDate < start) return false;
                      }

                      // End date at 23:59:59
                      if (endDate) {
                        const end = new Date(endDate);
                        end.setHours(23, 59, 59, 999);
                        if (invDate > end) return false;
                      }

                      return true;
                    });

                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);

                    if (paginatedInvoices.length === 0) {
                      return (
                        <tr>
                          <td colSpan={7} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                            {t('dashboard.invoices.empty')}
                          </td>
                        </tr>
                      );
                    }

                    return paginatedInvoices.map((invoice) => {
                      const commission = invoice.total * ((stats.commission_rate || 25) / 100);
                      const statusColors: { [key: string]: string } = {
                        PAID: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
                        OPEN: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
                        DRAFT: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
                        VOID: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
                        UNCOLLECTIBLE: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
                      };

                      return (
                        <tr key={invoice.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {invoice.invoice_number}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className="flex flex-col">
                              <div className="font-medium text-gray-900 dark:text-white">
                                {invoice.tenant.name}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {invoice.tenant.subdomain}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-white">
                            {invoice.subscription.plan.name}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                            ${invoice.total.toFixed(2)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-bold text-green-600 dark:text-green-400">
                            ${commission.toFixed(2)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[invoice.status] || 'bg-gray-100 text-gray-800'}`}>
                              {invoice.status}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {new Date(invoice.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {invoices.length > itemsPerPage && (
              <div className="border-t border-gray-200 px-6 py-4 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, invoices.length)} of {invoices.length} results
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(Math.ceil(invoices.length / itemsPerPage), currentPage + 1))}
                      disabled={currentPage >= Math.ceil(invoices.length / itemsPerPage)}
                      className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
